import { useQueryClient } from '@tanstack/react-query'
import { Alert, Button, Flex, Form, Spin } from 'antd'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import {
  useTreatmentServiceCreateHivAnamnesisCaseEndpoint,
  useTreatmentServiceGetHivAnamnesisCaseEndpoint,
  useTreatmentServiceGetHivAnamnesisCaseEndpointKey,
  useTreatmentServiceGetHivAnamnesisPrefillEndpoint,
  useTreatmentServiceUpdateHivAnamnesisCaseEndpoint
} from 'src/api/queries'
import {
  ApiError,
  HivAnamnesisCaseResponse,
  HivAnamnesisCaseStatus,
  HivAnamnesisFormSection,
  HivAnamnesisPrefillSource,
  TreatmentService
} from 'src/api/requests'
import {
  FormFooter,
  message,
  PageBreadcrumbs,
  SaveConfirm
} from 'src/components'
import { usePatientBreadcrumb } from 'src/features/patients/hooks/usePatientBreadcrumb'
import { useScrollToFirstError, ValidationError } from 'src/hooks'
import { useLocales } from 'src/locales'
import { formatDates, formErrorHandler, parseDates } from 'src/utils'

import {
  ClinicalCard,
  FamilyCard,
  HeaderInstitutionsCard,
  HivAnamnesisStatusCard,
  PatientCard,
  PrefillWarning,
  TestingCard,
  TransmissionCard,
  useSectionTitles
} from '../components'
import { AnnualUpdateModal } from '../components/AnnualUpdateModal'
import { HivAnamnesisFormProvider } from '../context/HivAnamnesisFormContext'
import { mergeWithSystem, toHivAnamnesisPayload } from '../utils/payload'

export const HivAnamnesisPage = () => {
  const { t } = useLocales()
  const { personId } = useParams()
  const queryClient = useQueryClient()

  const [form] = Form.useForm()
  const { onScrollToError } = useScrollToFirstError(form)

  const { breadcrumbItems: items, isLoadingPerson } =
    usePatientBreadcrumb(personId)

  const { data: existing, isLoading: isLoadingCase } =
    useTreatmentServiceGetHivAnamnesisCaseEndpoint(
      { personId: String(personId) },
      [personId],
      { enabled: !!personId, retry: false }
    )

  const isNew = !isLoadingCase && !existing?.id
  const isCompleted = existing?.status === HivAnamnesisCaseStatus.COMPLETED

  // The yearly ECDC update is recorded on a completed form, outside editing.
  const [isAnnualUpdateOpen, setIsAnnualUpdateOpen] = useState(false)

  // A new form opens in edit mode; a saved one opens read-only.
  const [isEditing, setIsEditing] = useState(false)

  // The prefill is loaded once, for a new form only.
  const {
    data: prefill,
    isLoading: isLoadingPrefill,
    isError: isPrefillError
  } = useTreatmentServiceGetHivAnamnesisPrefillEndpoint(
    { personId: String(personId), sections: [] },
    [personId],
    { enabled: !!personId && isNew, retry: false }
  )

  useEffect(() => {
    if (isNew) setIsEditing(true)
  }, [isNew])

  useEffect(() => {
    if (!prefill?.form || !isNew) return
    form.setFieldsValue({ form: parseDates(prefill.form) })
  }, [prefill, isNew])

  const resetToStored = () => {
    if (!existing?.form) return
    form.resetFields()
    form.setFieldsValue({ form: parseDates(existing.form) })
  }

  useEffect(() => {
    resetToStored()
  }, [existing])

  const onSaved = (response: HivAnamnesisCaseResponse, isComplete: boolean) => {
    message.success(
      t(
        isComplete
          ? 'hiv_anamnesis.success_complete'
          : 'hiv_anamnesis.success_draft'
      )
    )
    queryClient.setQueryData(
      [useTreatmentServiceGetHivAnamnesisCaseEndpointKey, personId],
      response
    )
    setIsEditing(false)
  }

  // Sources of the values taken with "Preia din sistem", for the prefill hints.
  const [refreshedSources, setRefreshedSources] = useState<
    HivAnamnesisPrefillSource[]
  >([])

  const sectionTitles = useSectionTitles()

  const refreshFromSystem = async (sections: HivAnamnesisFormSection[]) => {
    let response
    try {
      response = await TreatmentService.getHivAnamnesisPrefillEndpoint(
        String(personId),
        sections
      )
    } catch {
      // Nothing is changed: the doctor keeps what was entered and can try again.
      message.error(t('hiv_anamnesis.refresh.error'))

      return
    }
    const prefilled = response.form as Record<string, unknown> | undefined

    for (const section of sections) {
      const current = formatDates(form.getFieldValue(['form', section]) ?? {})
      form.setFieldValue(
        ['form', section],
        parseDates(mergeWithSystem(current, prefilled?.[section]))
      )
    }

    // setFieldValue does not validate: re-check the fields that show an error, so the ones the
    // system has just filled lose it.
    const withErrors = form
      .getFieldsError()
      .filter(
        ({ name, errors }) =>
          errors.length > 0 &&
          name[0] === 'form' &&
          sections.includes(name[1] as HivAnamnesisFormSection)
      )
      .map(({ name }) => name)
    if (withErrors.length > 0) {
      form.validateFields(withErrors).catch(() => undefined)
    }

    setRefreshedSources((previous) => [
      ...previous,
      ...(response.sources ?? [])
    ])

    const failedSections = response.failedSections ?? []
    if (failedSections.length > 0) {
      message.warning(
        t('hiv_anamnesis.refresh.partial', {
          sections: sectionTitles(failedSections)
        })
      )
    } else {
      message.success(t('hiv_anamnesis.refresh.success'))
    }
  }

  const onError = (err: ApiError) => {
    form.setFields(formErrorHandler(err.body?.errors))
  }

  const { mutate: create, isPending: isCreating } =
    useTreatmentServiceCreateHivAnamnesisCaseEndpoint({ onError })

  const { mutate: update, isPending: isUpdating } =
    useTreatmentServiceUpdateHivAnamnesisCaseEndpoint({ onError })

  const save = (complete: boolean) => {
    const values = form.getFieldsValue(true)
    const formData = toHivAnamnesisPayload(values.form)
    const onSuccess = (response: HivAnamnesisCaseResponse) =>
      onSaved(response, complete || isCompleted)

    if (existing?.id) {
      update(
        {
          id: existing.id,
          // The version this page loaded: a save over someone else's newer changes is rejected (409).
          requestBody: { form: formData, complete, version: existing.version }
        },
        { onSuccess }
      )
    } else {
      create(
        {
          requestBody: { personId: String(personId), form: formData, complete }
        },
        { onSuccess }
      )
    }
  }

  // A draft is saved as it is; completing (and every save of a completed form) validates first.
  const onSaveDraft = () => save(false)

  const onComplete = async () => {
    try {
      await form.validateFields()
    } catch (error) {
      onScrollToError(error as ValidationError)

      return
    }

    save(true)
  }

  const onCancelEdit = () => {
    resetToStored()
    setIsEditing(false)
  }

  const userHasAccessToPersonalData =
    (existing ?? prefill)?.userHasAccessToPersonalData ?? true

  const title = isNew ? t('hiv_anamnesis.add') : t('hiv_anamnesis.view')

  const breadcrumbItems = [...items, { title }]

  const isSaving = isCreating || isUpdating

  const saveDraftButton = (placement: 'leftBottom' | 'top') => (
    <SaveConfirm
      title={t('hiv_anamnesis.confirm_draft')}
      onConfirm={onSaveDraft}
      placement={placement}
      okButtonProps={{ loading: isSaving }}
    >
      <Button disabled={isSaving}>{t('hiv_anamnesis.save_draft')}</Button>
    </SaveConfirm>
  )

  // While editing, the same buttons sit both at the top and in the footer:
  // a draft has Cancel / Save draft / Complete, a completed form Cancel / Save.
  const editActions = isCompleted ? (
    <Flex gap={8}>
      <Button onClick={onCancelEdit} disabled={isSaving}>
        {t('common.cancel')}
      </Button>

      <SaveConfirm
        title={t('hiv_anamnesis.confirm_save')}
        onConfirm={() => void onComplete()}
        placement="leftBottom"
        okButtonProps={{ loading: isSaving }}
      >
        <Button type="primary" disabled={isSaving}>
          {t('hiv_anamnesis.save')}
        </Button>
      </SaveConfirm>
    </Flex>
  ) : (
    <Flex gap={8}>
      {!isNew && (
        <Button onClick={onCancelEdit} disabled={isSaving}>
          {t('common.cancel')}
        </Button>
      )}

      {saveDraftButton('leftBottom')}

      <SaveConfirm
        title={t('hiv_anamnesis.confirm_complete')}
        onConfirm={() => void onComplete()}
        placement="leftBottom"
        okButtonProps={{ loading: isSaving }}
      >
        <Button type="primary" disabled={isSaving}>
          {t('hiv_anamnesis.complete')}
        </Button>
      </SaveConfirm>
    </Flex>
  )

  const actions = isEditing ? (
    editActions
  ) : (
    <Flex gap={8}>
      {isCompleted && (
        <Button onClick={() => setIsAnnualUpdateOpen(true)}>
          {t('hiv_anamnesis.annual_update.record')}
        </Button>
      )}

      <Button type="primary" onClick={() => setIsEditing(true)}>
        {t('common.edit')}
      </Button>
    </Flex>
  )

  if (isLoadingPerson || isLoadingCase || (isNew && isLoadingPrefill)) {
    return <Spin spinning fullscreen />
  }

  return (
    <PageBreadcrumbs
      title={title}
      items={breadcrumbItems}
      showBack
      extra={actions}
      extraProps={{ align: 'flex-end' }}
    >
      <HivAnamnesisFormProvider
        sources={[
          ...((isNew ? prefill?.sources : undefined) ?? []),
          ...refreshedSources
        ]}
        userHasAccessToPersonalData={userHasAccessToPersonalData}
        isEditing={isEditing}
        refreshFromSystem={refreshFromSystem}
      >
        <Form
          layout="vertical"
          requiredMark={false}
          form={form}
          disabled={!isEditing}
        >
          <Flex vertical gap={24}>
            {!userHasAccessToPersonalData && (
              <Alert
                type="info"
                showIcon
                message={t('hiv_anamnesis.no_personal_data_title')}
                description={t('hiv_anamnesis.no_personal_data_description')}
              />
            )}

            {isNew && (
              <PrefillWarning
                isError={isPrefillError}
                failedSections={prefill?.failedSections}
              />
            )}

            <HivAnamnesisStatusCard hivAnamnesisCase={existing} />
            <HeaderInstitutionsCard />
            <PatientCard />
            <TestingCard />
            <TransmissionCard />
            <FamilyCard />
            <ClinicalCard hivAnamnesisCase={existing} />
          </Flex>

          <FormFooter
            hidden={!isEditing}
            onCancel={isNew ? undefined : onCancelEdit}
            onSave={() => void onComplete()}
            saveConfirmTitle={t(
              isCompleted
                ? 'hiv_anamnesis.confirm_save'
                : 'hiv_anamnesis.confirm_complete'
            )}
            saveLabel={t(
              isCompleted ? 'hiv_anamnesis.save' : 'hiv_anamnesis.complete'
            )}
            extra={!isCompleted && saveDraftButton('top')}
            loading={isSaving}
            disabled={isSaving}
          />
        </Form>
      </HivAnamnesisFormProvider>

      {isAnnualUpdateOpen && existing?.id && (
        <AnnualUpdateModal
          hivAnamnesisCase={existing}
          onClose={() => setIsAnnualUpdateOpen(false)}
        />
      )}
    </PageBreadcrumbs>
  )
}
